<template>
  <myDialog  :isDialog="isDialog" :title="isEdit ? '修改企业信用评价结果' : '添加企业信用评价结果'" :width="'800px'" @handleClose="handleClose">
    <template slot="content">
      <el-form :model="formData" :rules="rules" ref="form" label-width="180px">
        <!-- 企业名称 -->
        <el-form-item label="企业名称" prop="enterpriseName" required>
          <el-input v-model="formData.enterpriseName" placeholder="请输入企业名称" maxlength="200" show-word-limit></el-input>
        </el-form-item>

        <!-- 统一社会信用代码 -->
        <el-form-item label="统一社会信用代码" prop="unifiedSocialCreditIdentifier" required>
          <el-input v-model="formData.unifiedSocialCreditIdentifier" placeholder="请输入统一社会信用代码" maxlength="100"></el-input>
        </el-form-item>

        <!-- 季度评价 -->
        <el-form-item label="季度评价" prop="evaluateQuarter" required>
          <el-input v-model="formData.evaluateQuarter" placeholder="请输入季度评价" maxlength="100"></el-input>
        </el-form-item>

        <!-- 评价结果 -->
        <el-form-item label="评价结果" prop="evaluateResultCode" required>
          <el-select
            v-model="formData.evaluateResultCode"
            placeholder="请选择评价结果"
            @change="handleEvaluateResultChange"
            filterable
            clearable
            style="width: 100%;"
          >
            <el-option
              v-for="item in evaluateResultOptions"
              :key="item.dictValue"
              :label="item.dictLabel"
              :value="item.dictValue"
            >
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
    </template>
    <template slot="footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="loading">保存</el-button>
    </template>
  </myDialog>
</template>

<script>
import { addCreditRating, updateCreditRating } from '@/api/employment'
import { getListByDictType } from '@/api/sys/dictData'

export default {
  name: 'PolicyDialog',
  props: {
    isDialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {
        enterpriseName: '',
        unifiedSocialCreditIdentifier: '',
        evaluateQuarter: '',
        evaluateResultCode: '',
        evaluateResultName: ''
      },
      evaluateResultOptions: [],
      loading: false,
      rules: {
        enterpriseName: [
          { required: true, message: '请输入企业名称', trigger: 'blur' }
        ],
        unifiedSocialCreditIdentifier: [
          { required: true, message: '请输入统一社会信用代码', trigger: 'blur' }
        ],
        evaluateQuarter: [
          { required: true, message: '请输入季度评价', trigger: 'blur' }
        ],
        evaluateResultCode: [
          { required: true, message: '请选择评价结果', trigger: 'change' }
        ]
      }
    }
  },
  watch: {
    editData: {
      handler(newVal) {
        if (newVal && this.isEdit && Object.keys(newVal).length > 0) {
          this.formData = {
            id: newVal.id,
            enterpriseName: newVal.enterpriseName || '',
            unifiedSocialCreditIdentifier: newVal.unifiedSocialCreditIdentifier || '',
            evaluateQuarter: newVal.evaluateQuarter || '',
            evaluateResultCode: newVal.evaluateResultCode || '',
            evaluateResultName: newVal.evaluateResultName || ''
          }
          // 如果只有 evaluateResultCode 没有 evaluateResultName，从选项中查找
          if (this.formData.evaluateResultCode && !this.formData.evaluateResultName) {
            const option = this.evaluateResultOptions.find(item => item.dictValue === this.formData.evaluateResultCode)
            if (option) {
              this.formData.evaluateResultName = option.dictLabel
            }
          }
        }
      },
      immediate: true,
      deep: true
    },
    isDialog(val) {
      if (!val) {
        this.resetForm()
      }
    },
    evaluateResultOptions: {
      handler() {
        // 当评价结果选项加载完成后，如果编辑模式下有 evaluateResultCode，确保 evaluateResultName 也被设置
        if (this.isEdit && this.formData.evaluateResultCode && this.evaluateResultOptions.length > 0) {
          const option = this.evaluateResultOptions.find(item => item.dictValue === this.formData.evaluateResultCode)
          if (option && !this.formData.evaluateResultName) {
            this.formData.evaluateResultName = option.dictLabel
          }
        }
      },
      immediate: true
    }
  },
  created() {
    this.getEvaluateResultOptions()
  },
  methods: {
    // 获取评价结果选项
    async getEvaluateResultOptions() {
      const res = await getListByDictType('institution_credit_rating_evaluate_result')
      if (res && res.code === 200 && res.data) {
        this.evaluateResultOptions = res.data || []
      }
    },
    // 评价结果选择变化
    handleEvaluateResultChange(value) {
      const selectedOption = this.evaluateResultOptions.find(item => item.dictValue === value)
      if (selectedOption) {
        this.formData.evaluateResultCode = selectedOption.dictValue
        this.formData.evaluateResultName = selectedOption.dictLabel
      } else {
        this.formData.evaluateResultCode = ''
        this.formData.evaluateResultName = ''
      }
      // 触发验证
      this.$refs.form && this.$refs.form.validateField('evaluateResultCode')
    },
    // 提交
    handleSubmit() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.loading = true
          
          // 构建提交数据
          const submitData = {
            enterpriseName: this.formData.enterpriseName,
            unifiedSocialCreditIdentifier: this.formData.unifiedSocialCreditIdentifier,
            evaluateQuarter: this.formData.evaluateQuarter,
            evaluateResultCode: this.formData.evaluateResultCode,
            evaluateResultName: this.formData.evaluateResultName
          }
          
          // 如果是编辑，需要传递 id
          if (this.isEdit && this.formData.id) {
            submitData.id = this.formData.id
          }

          const api = this.isEdit ? updateCreditRating : addCreditRating
          api(submitData).then(res => {
            if (res && res.code === 200) {
              this.$message.success(this.isEdit ? '修改成功' : '添加成功')
              this.$emit('success')
              this.handleClose()
            } else {
              this.$message.error(res.msg || '操作失败')
            }
          }).catch(err => {
            console.error('请求出错', err)
            this.$message.error('操作失败')
          }).finally(() => {
            this.loading = false
          })
        }
      })
    },
    // 关闭
    handleClose() {
      this.$emit('handleClose')
      this.resetForm()
    },
    // 重置表单
    resetForm() {
      this.$refs.form && this.$refs.form.resetFields()
      this.formData = {
        enterpriseName: '',
        unifiedSocialCreditIdentifier: '',
        evaluateQuarter: '',
        evaluateResultCode: '',
        evaluateResultName: ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .el-form-item__label {
  .el-form-item.is-required &::before {
    content: '*';
    color: #f56c6c;
    margin-right: 4px;
  }
}

/deep/ .el-form-item__label {
    color: #2A3347 !important;
    font-weight: 400;
    font-size: 16px;
}

/deep/ .el-dialog__header {
    background: #297DEE !important;
   
}

/deep/ .el-dialog__title {
    color: #ffffff !important;
    font-weight: 700;
    font-size: 17px;
}

/deep/ .el-dialog__headerbtn {
  filter: brightness(0) invert(1) !important;
}
</style>
